import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { formatCurrency } from './radarUtils';

const CapitalParadoPage = () => {
  const [loading, setLoading] = useState(false);
  const [busca, setBusca] = useState('');
  const [secao, setSecao] = useState('todas');
  const [ordenacao, setOrdenacao] = useState('valor_desc');
  const [data, setData] = useState({ itens: [], valor_total: 0 });

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/radar-lucro/capital-parado');
      setData(response.data || { itens: [], valor_total: 0 });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar capital parado');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const secoes = useMemo(() => {
    const set = new Set((data.itens || []).map((i) => i.secao).filter(Boolean));
    return Array.from(set).sort((a, b) => String(a).localeCompare(String(b)));
  }, [data.itens]);

  const itensFiltrados = useMemo(() => {
    const texto = busca.trim().toLowerCase();
    const base = (data.itens || []).filter((item) => {
      const matchBusca =
        !texto ||
        String(item.descricao || '').toLowerCase().includes(texto) ||
        String(item.codigoean || '').toLowerCase().includes(texto);
      const matchSecao = secao === 'todas' || (item.secao || '') === secao;
      return matchBusca && matchSecao;
    });
    const sorted = [...base];
    if (ordenacao === 'valor_desc') sorted.sort((a, b) => Number(b.valor_parado || 0) - Number(a.valor_parado || 0));
    if (ordenacao === 'valor_asc') sorted.sort((a, b) => Number(a.valor_parado || 0) - Number(b.valor_parado || 0));
    if (ordenacao === 'estoque_asc') sorted.sort((a, b) => Number(a.estoque_atual || 0) - Number(b.estoque_atual || 0));
    if (ordenacao === 'estoque_desc') sorted.sort((a, b) => Number(b.estoque_atual || 0) - Number(a.estoque_atual || 0));
    return sorted;
  }, [data.itens, busca, secao, ordenacao]);

  const valorFiltrado = useMemo(() => itensFiltrados.reduce((acc, i) => acc + Number(i.valor_parado || 0), 0), [itensFiltrados]);

  return (
    <div className="animate-fade-in space-y-4">
      <div className="rounded-xl border border-warning-200 bg-warning-50 p-4">
        <p className="text-sm font-semibold text-warning-800">Caixa preso em estoque</p>
        <p className="text-2xl font-bold text-warning-900 mt-1">{formatCurrency(valorFiltrado)} no filtro atual</p>
        <p className="text-sm text-warning-800 mt-1">Voce pode estar financiando estoque parado em vez de proteger liquidez.</p>
      </div>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Capital Parado</h1>
          <p className="text-gray-600">Estoque sem giro ou muito acima do necessario</p>
        </div>
        <button className="btn-secondary" onClick={loadData} disabled={loading}>
          Atualizar
        </button>
      </div>

      <div className="card">
        <div className="card-body grid grid-cols-1 md:grid-cols-4 gap-3">
          <input className="form-input" placeholder="Buscar por descricao ou EAN" value={busca} onChange={(e) => setBusca(e.target.value)} />
          <select className="form-select" value={secao} onChange={(e) => setSecao(e.target.value)}>
            <option value="todas">Todas as secoes</option>
            {secoes.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select className="form-select" value={ordenacao} onChange={(e) => setOrdenacao(e.target.value)}>
            <option value="valor_desc">Maior valor parado</option>
            <option value="valor_asc">Menor valor parado</option>
            <option value="estoque_asc">Estoque asc</option>
            <option value="estoque_desc">Estoque desc</option>
          </select>
          <div className="text-sm text-gray-600 flex items-center">Itens filtrados: {itensFiltrados.length}</div>
        </div>
      </div>

      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>EAN</th>
                <th>Produto</th>
                <th>Secao</th>
                <th>Estoque</th>
                <th>CMD</th>
                <th>Cobertura</th>
                <th>Valor parado</th>
              </tr>
            </thead>
            <tbody>
              {itensFiltrados.map((item) => (
                <tr key={item.id_produto}>
                  <td>{item.codigoean || '-'}</td>
                  <td>{item.descricao}</td>
                  <td>{item.secao || '-'}</td>
                  <td>{item.estoque_atual}</td>
                  <td>{item.cmd}</td>
                  <td>{item.cobertura_dias ?? '-'}</td>
                  <td>{formatCurrency(item.valor_parado)}</td>
                </tr>
              ))}
              {!loading && itensFiltrados.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-gray-500 py-8">
                    Nenhum item com capital parado no momento.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CapitalParadoPage;
